/**
 * The Maxi-Paket bundles an Eliteclub card with one drink. It carries the
 * "drinks" tag for tag-rule purposes, which alone would render it as the
 * generic cup — reading as a drinks flat rather than as the bundle it is.
 * Matched by name because the bundle has no tag of its own.
 *
 * @param {string} name - Name des Pakets bzw. Upsells
 * @returns {boolean}
 */
export const isMaxiPackageName = name => {
  if (!name || typeof name !== 'string') return false;
  return name.toLowerCase().replace(/[^a-z0-9]/g, '') === 'maxipaket';
};

/**
 * Hilfsfunktion zum Extrahieren der Upgrade-Symbole aus einem Booking-Objekt
 *
 * @param {Object} booking - Das Booking-Objekt
 * @returns {Array} - Array mit den entsprechenden Upgrade-Symbolen
 */
export const extractUpgradeSymbols = booking => {
  const upgradeSymbols = new Set();

  const addUpgradeSymbol = symbol => {
    if (!symbol || typeof symbol !== 'string') return;
    upgradeSymbols.add(symbol);
  };

  // Track where a "drinks" tag comes from: the booking-level tags array mixes
  // package and upsell tags without provenance, so the named sources decide
  // whether the cup still stands for something other than the Maxi-Paket.
  let hasMaxiPackage = false;
  let hasOtherDrinksSource = false;
  const noteDrinksSource = (name, tags) => {
    const isMaxi = isMaxiPackageName(name);
    if (isMaxi) {
      hasMaxiPackage = true;
    } else if (Array.isArray(tags) && tags.includes('drinks')) {
      hasOtherDrinksSource = true;
    }
  };

  const normalizeSpecialUpgrade = raw => {
    if (!raw || typeof raw !== 'string') return null;

    switch (raw) {
      case 'iconCakeRed':
        return 'childBirthdayFull';
      case 'iconCakeOrange':
        return 'childBirthday';
      default:
        return raw;
    }
  };

  // Überprüfe selectedPackage
  if (booking.selectedPackage && booking.selectedPackage.name) {
    const packageName = booking.selectedPackage.name;

    if (packageName === 'Kindergeburtstag All-Inc') {
      addUpgradeSymbol('childBirthdayFull');
    } else if (packageName === 'Kindergeburtstag Basic') {
      addUpgradeSymbol('childBirthday');
    } else if (packageName === 'Junggesellen-Abschied') {
      addUpgradeSymbol('bachelorParty');
    } else if (packageName === 'Erwachsenengeburtstag') {
      addUpgradeSymbol('adultBirthday');
    }
  }

  noteDrinksSource(booking.selectedPackage?.name, booking.selectedPackage?.tags);

  // Tag-driven symbols: any tag on the booked package flows into the card, so
  // new packages only need a tag in the database, no code change.
  if (booking.selectedPackage && Array.isArray(booking.selectedPackage.tags)) {
    booking.selectedPackage.tags
      .map(normalizeSpecialUpgrade)
      .filter(Boolean)
      .forEach(addUpgradeSymbol);
  }

  // Check specialUpgrades/tags to ensure consistent symbols across entertainments
  if (booking.specialUpgrades) {
    const specialUpgrades = Array.isArray(booking.specialUpgrades)
      ? booking.specialUpgrades
      : [booking.specialUpgrades];
    specialUpgrades
      .map(normalizeSpecialUpgrade)
      .filter(Boolean)
      .forEach(addUpgradeSymbol);
  }

  if (Array.isArray(booking.tags)) {
    booking.tags
      .map(normalizeSpecialUpgrade)
      .filter(Boolean)
      .forEach(addUpgradeSymbol);
  }

  // Überprüfe upsells
  if (booking.upsells && Array.isArray(booking.upsells)) {
    booking.upsells.forEach(upsell => {
      if (upsell.name === 'Wundertüte Geschenk') {
        addUpgradeSymbol('bag');
      } else if (upsell.name.includes('Pizzablech')) {
        addUpgradeSymbol('pizza');
      }
      noteDrinksSource(upsell.name, upsell.tags);
      // Tag-driven symbols for upsells, same mechanism as packages.
      if (Array.isArray(upsell.tags)) {
        upsell.tags
          .map(normalizeSpecialUpgrade)
          .filter(Boolean)
          .forEach(addUpgradeSymbol);
      }
    });
  }

  // Prefer the more specific birthday icon over the generic one.
  // Example: tags might contain both "childBirthday" and "iconCakeRed".
  if (upgradeSymbols.has('childBirthdayFull')) {
    upgradeSymbols.delete('childBirthday');
  }
  // Teen packages carry the childBirthday tag for tag-rule purposes; the more
  // specific teen icon wins over the generic child cake.
  if (upgradeSymbols.has('teenBirthday')) {
    upgradeSymbols.delete('childBirthday');
  }

  if (hasMaxiPackage) {
    addUpgradeSymbol('maxiPackage');
    // The bundle's own "drinks" tag also reaches booking.tags, so dropping it
    // at the source is not enough. Keep the cup only when a separate drinks
    // upgrade is booked as well.
    if (!hasOtherDrinksSource) {
      upgradeSymbols.delete('drinks');
    }
  }

  return Array.from(upgradeSymbols);
};
